import {Component} from 'panel';

import {registerMPElement} from '../../util/register-element';
import {eventWasOutside} from '../../util/dom';

import {onAnimationEnd, offAnimationEnd} from '../../util/dom';

import template from './index.jade';
import css from './index.styl?inline';

const VISIBILITY_OPEN = `open`;
const VISIBILITY_OPENING = `opening`;
const VISIBILITY_CLOSED = `closed`;
const VISIBILITY_CLOSING = `closing`;

export const DIRECTIONS = {
  UP: `up`,
  DOWN: `down`,
};

export default registerMPElement(
  `mp-drop-menu`,
  class extends Component {
    static get attrsSchema() {
      return {
        open: {
          description: `Is the dropdown menu open?`,
          type: `boolean`,
          default: false,
        },
        direction: {
          description: `Menu opens on the top of an element if 'up', on the bottom if 'down'`,
          type: `string`,
          enum: Object.values(DIRECTIONS),
          default: DIRECTIONS.DOWN,
        },
        'with-caret': {
          description: `Show a little caret pointer?`,
          type: `boolean`,
          default: false,
        },
        'caret-pos': {
          description: `position of caret, if caret is shown`,
          type: `json`,
          interface: `CaretPos`,
          example: {left: `20`},
        },
        'caret-pos-left': {
          description: `DEPRECATED: position of caret from left. use 'caret-pos' as it supports both left and right`,
          type: `string`,
          default: `50%`,
        },
        'prevent-close-selectors': {
          description: `dropmenu is not closed if the clicked element matches any of the css selectors`,
          type: `json`,
          default: [],
          interface: `PreventCloseSelectors`,
          example: [`#help-button`, `.widget-hover-fill`],
        },
      };
    }

    get config() {
      return {
        css,
        template,
        useShadowDom: true,

        defaultState: {
          visibility: VISIBILITY_CLOSED,
        },
      };
    }

    get caretPos() {
      return this.attr(`caret-pos`) || {left: this.attr(`caret-pos-left`)};
    }

    _closeMenu() {
      switch (this.state.visibility) {
        case VISIBILITY_CLOSED:
        case VISIBILITY_CLOSING:
          break;
        case VISIBILITY_OPENING:
          this.update({visibility: VISIBILITY_CLOSED});
          this.dispatchCurrentVisibility();
          break;
        case VISIBILITY_OPEN:
          this.update({visibility: VISIBILITY_CLOSING});
          break;
      }
    }

    _openMenu() {
      switch (this.state.visibility) {
        case VISIBILITY_OPEN:
        case VISIBILITY_OPENING:
          break;
        case VISIBILITY_CLOSING:
          this.update({visibility: VISIBILITY_OPEN});
          this.dispatchCurrentVisibility();
          break;
        case VISIBILITY_CLOSED:
          this.update({visibility: VISIBILITY_OPENING});
          break;
      }
    }

    dispatchCurrentVisibility() {
      this.dispatchEvent(new CustomEvent(`change`, {detail: {state: this.state.visibility}}));
    }

    attributeChangedCallback(attr, oldVal, newVal) {
      super.attributeChangedCallback(attr, oldVal, newVal);
      if (attr === `open` && this.initialized) {
        if (this.attr(`open`)) {
          this._openMenu();
        } else {
          this._closeMenu();
        }
      }
    }

    connectedCallback() {
      super.connectedCallback();

      if (this.attr(`open`)) {
        this._openMenu();
      }
      this._onAnimationEnd = () => {
        switch (this.state.visibility) {
          case VISIBILITY_OPENING:
            this.update({visibility: VISIBILITY_OPEN});
            // Use a capturing event listener so we can listen for events before they bubble
            // mp-modal eats bubbling events with event.stopPropagation(). Captured events always trigger
            document.body.addEventListener(`keydown`, this.maybeCloseOnEscape, true);
            document.body.addEventListener(`mousedown`, this.mousedownOutsideListener, true);
            this.dispatchCurrentVisibility();
            break;
          case VISIBILITY_CLOSING:
            this.update({visibility: VISIBILITY_CLOSED});
            document.body.removeEventListener(`keydown`, this.maybeCloseOnEscape, true);
            document.body.removeEventListener(`mousedown`, this.mousedownOutsideListener, true);
            this.dispatchCurrentVisibility();
            break;
        }
      };
      onAnimationEnd(this.el, this._onAnimationEnd);

      // close the menu when escape is pressed
      this.maybeCloseOnEscape = (ev) => {
        if (!ev.defaultPrevented && ev.keyCode === 27) {
          this._closeMenu();
        }
      };

      // close when the user does mousedown outside of the menu
      this.mousedownOutsideListener = (ev) => {
        const path = window.ShadowDOMPolyfill || typeof ev.composedPath !== `function` ? ev.path : ev.composedPath();
        const mousedownWithinPreventCloseElement = this.attr(`prevent-close-selectors`).some((selector) =>
          path.some((el) => el.matches && el.matches(selector)),
        );
        if (this.attr(`open`) && eventWasOutside(ev, this) && !mousedownWithinPreventCloseElement) {
          this._closeMenu();
        }
      };
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      offAnimationEnd(this.el, this._onAnimationEnd);
      document.body.removeEventListener(`keydown`, this.maybeCloseOnEscape, true);
      document.body.removeEventListener(`mousedown`, this.mousedownOutsideListener, true);
    }
  },
);
